import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Card,
  EmptyState,
  SectionHeader,
  Skeleton,
  Tag,
} from "../../../design-system";
import {
  IconChart,
  IconSparkles,
  IconUsers,
} from "../../../design-system/icons/icons";
import {
  ArtifactDashboard,
  ArtifactDashboardRow,
  ArtifactEventTotals,
  getArtifactDashboard,
  revokeArtifactPublicationAsCoach,
} from "../../../database/analytics/artifactDashboardApi";

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
`;

const Intro = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--space-4);
  align-items: flex-start;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const IntroCopy = styled.div`
  max-width: 720px;

  h2 {
    margin: 0 0 var(--space-2);
    font-size: var(--font-size-xl);
    color: var(--color-text);
  }

  p {
    margin: 0;
    color: var(--color-text-secondary);
    line-height: var(--line-height-relaxed);
  }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-4);

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const MetricLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const MetricValue = styled.div`
  margin-top: var(--space-3);
  color: var(--color-text);
  font-size: 30px;
  line-height: 1;
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
`;

const MetricHint = styled.div`
  margin-top: var(--space-2);
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);
`;

const FunnelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(112px, 1fr));
  gap: var(--space-2);
  overflow-x: auto;
  padding-bottom: var(--space-1);
`;

const FunnelStep = styled.div`
  position: relative;
  min-width: 112px;
  padding: var(--space-4);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);

  &:not(:last-child)::after {
    content: "→";
    position: absolute;
    right: -10px;
    top: 50%;
    z-index: 1;
    width: 18px;
    height: 18px;
    margin-top: -9px;
    text-align: center;
    color: var(--color-text-tertiary);
    background: var(--color-surface);
    border-radius: var(--radius-full);
  }
`;

const FunnelLabel = styled.div`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FunnelValue = styled.div`
  margin-top: var(--space-2);
  color: var(--color-text);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
`;

const FunnelRate = styled.div`
  margin-top: var(--space-1);
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);
`;

const TrendChart = styled.div`
  height: 190px;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding-top: var(--space-5);
  overflow: hidden;
`;

const TrendColumn = styled.div`
  flex: 1;
  min-width: 5px;
  height: 100%;
`;

const TrendBars = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  gap: 2px;
`;

const TrendBar = styled.div<{ $height: number; $accent?: boolean }>`
  flex: 1;
  min-height: 2px;
  height: ${(props) => `${Math.max(props.$height, 1)}%`};
  background: ${(props) =>
    props.$accent ? "var(--color-success)" : "var(--color-accent)"};
  opacity: ${(props) => (props.$accent ? 0.8 : 0.5)};
  border-radius: 3px 3px 0 0;
`;

const TrendLegend = styled.div`
  display: flex;
  gap: var(--space-4);
  margin-top: var(--space-3);
  color: var(--color-text-tertiary);
  font-size: var(--font-size-xs);

  span::before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 6px;
    border-radius: 2px;
    background: var(--color-accent);
  }

  span:last-child::before {
    background: var(--color-success);
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
  font-size: var(--font-size-sm);

  th {
    padding: var(--space-3) var(--space-4);
    text-align: left;
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid var(--color-border);
  }

  td {
    padding: var(--space-4);
    color: var(--color-text-secondary);
    border-bottom: 1px solid var(--color-border);
    vertical-align: middle;
    font-variant-numeric: tabular-nums;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

const ArtifactName = styled.div`
  max-width: 300px;

  strong {
    display: block;
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    margin-top: 3px;
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RowActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-1);
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.7fr);
  gap: var(--space-4);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RankingRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-4);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: 0;
  }

  strong {
    display: block;
    color: var(--color-text);
  }

  small {
    color: var(--color-text-tertiary);
  }
`;

const RankingMetrics = styled.div`
  text-align: right;
  color: var(--color-text-secondary);
  font-variant-numeric: tabular-nums;

  small {
    display: block;
  }
`;

const ErrorText = styled.div`
  color: var(--color-danger);
`;

const count = (totals: ArtifactEventTotals | undefined, key: keyof ArtifactEventTotals) =>
  totals?.[key] || 0;

const rate = (numerator: number, denominator: number): string =>
  denominator > 0 ? `${((numerator / denominator) * 100).toFixed(1)}%` : "—";

const kindLabel = (value: string): string =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString() : "—";

const MetricCard: React.FC<{
  label: string;
  value: string | number;
  hint: string;
  icon: React.ReactNode;
}> = ({ label, value, hint, icon }) => (
  <Card padding="md">
    <MetricLabel>
      {icon}
      {label}
    </MetricLabel>
    <MetricValue>
      {typeof value === "number" ? value.toLocaleString() : value}
    </MetricValue>
    <MetricHint>{hint}</MetricHint>
  </Card>
);

type Props = {
  communityId?: string;
  days: number;
};

const ArtifactTab: React.FC<Props> = ({ communityId, days }) => {
  const [dashboard, setDashboard] = useState<ArtifactDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      setDashboard(await getArtifactDashboard(communityId, days));
    } catch (nextError) {
      setError(
        (nextError as Error)?.message || "Could not load artifact analytics."
      );
    } finally {
      setLoading(false);
    }
  }, [communityId, days]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = dashboard?.summary.totals;
  const views = count(totals, "artifact_view");
  const completions = count(totals, "artifact_complete");
  const remixes = count(totals, "artifact_remix_created");
  const joins = count(totals, "artifact_community_join_attributed");

  const trend = useMemo(
    () => (dashboard?.daily || []).slice(-30),
    [dashboard?.daily]
  );
  const trendMax = Math.max(
    1,
    ...trend.map((day) =>
      Math.max(day.events.artifact_view, day.events.artifact_complete)
    )
  );

  const copyLink = async (artifact: ArtifactDashboardRow) => {
    if (!artifact.url) return;
    await navigator.clipboard?.writeText(artifact.url);
    setCopiedId(artifact.id);
    window.setTimeout(() => setCopiedId(null), 1500);
  };

  const revoke = async (artifact: ArtifactDashboardRow) => {
    if (
      !communityId ||
      !window.confirm(
        `Revoke “${artifact.title}”? Its public link will stop working immediately. The member's private artifact will not be deleted.`
      )
    ) {
      return;
    }
    setRevokingId(artifact.id);
    setError("");
    try {
      await revokeArtifactPublicationAsCoach(communityId, artifact.id);
      await load();
    } catch (nextError) {
      setError(
        (nextError as Error)?.message || "Could not revoke this publication."
      );
    } finally {
      setRevokingId(null);
    }
  };

  if (!communityId) {
    return (
      <EmptyState
        title="Choose a community"
        description="Artifact analytics are scoped to the coach's current community."
      />
    );
  }

  if (loading && !dashboard) {
    return (
      <SectionStack>
        <MetricGrid>
          {[0, 1, 2, 3].map((item) => (
            <Card key={item} padding="md">
              <Skeleton width="45%" height={14} />
              <div style={{ marginTop: 16 }}>
                <Skeleton width="65%" height={30} />
              </div>
            </Card>
          ))}
        </MetricGrid>
        <Card padding="lg">
          <Skeleton height={190} />
        </Card>
      </SectionStack>
    );
  }

  if (!dashboard?.summary.publications) {
    return (
      <SectionStack>
        <Intro>
          <IntroCopy>
            <h2>Artifact growth loop</h2>
            <p>
              Follow shared tools from public interaction through private saves,
              attributed signups, and community joins.
            </p>
          </IntroCopy>
          <Button size="sm" variant="secondary" onClick={load} isLoading={loading}>
            Refresh
          </Button>
        </Intro>
        {error && <ErrorText>{error}</ErrorText>}
        <EmptyState
          icon={<IconSparkles size={20} />}
          title="No published artifacts yet"
          description="When a member publishes an eligible lesson artifact, its public funnel and source lesson will appear here."
        />
      </SectionStack>
    );
  }

  const funnel = [
    { label: "Views", value: views },
    { label: "Started", value: count(totals, "artifact_start") },
    { label: "Completed", value: completions },
    { label: "CTA", value: count(totals, "artifact_cta") },
    { label: "Saved", value: remixes },
    { label: "Signups", value: count(totals, "artifact_signup_attributed") },
    { label: "Joined", value: joins },
  ];

  return (
    <SectionStack>
      <Intro>
        <IntroCopy>
          <h2>Artifact growth loop</h2>
          <p>
            Public interaction and acquisition from member-shared tools. Counts
            are raw events in the selected range; conversion percentages use
            those event totals rather than unique visitors.
          </p>
        </IntroCopy>
        <Button size="sm" variant="secondary" onClick={load} isLoading={loading}>
          Refresh
        </Button>
      </Intro>

      {error && (
        <Card padding="md">
          <ErrorText>{error}</ErrorText>
        </Card>
      )}

      <MetricGrid>
        <MetricCard
          label="Active publications"
          value={dashboard.summary.activePublications}
          hint={`${dashboard.summary.publications.toLocaleString()} total including revoked`}
          icon={<IconSparkles size={15} />}
        />
        <MetricCard
          label="Public views"
          value={views}
          hint={`${count(totals, "artifact_share").toLocaleString()} share events`}
          icon={<IconChart size={15} />}
        />
        <MetricCard
          label="Completion rate"
          value={rate(completions, views)}
          hint={`${completions.toLocaleString()} completions`}
          icon={<IconChart size={15} />}
        />
        <MetricCard
          label="Community join rate"
          value={rate(joins, views)}
          hint={`${joins.toLocaleString()} attributed joins · ${remixes.toLocaleString()} saves`}
          icon={<IconUsers size={15} />}
        />
      </MetricGrid>

      <Card padding="lg">
        <SectionHeader
          title="Acquisition funnel"
          description={`${dashboard.range.startDay} to ${dashboard.range.endDay}`}
        />
        <FunnelGrid>
          {funnel.map((step, index) => (
            <FunnelStep key={step.label}>
              <FunnelLabel>{step.label}</FunnelLabel>
              <FunnelValue>{step.value.toLocaleString()}</FunnelValue>
              <FunnelRate>
                {index === 0
                  ? "Selected range"
                  : `${rate(step.value, funnel[index - 1].value)} from previous`}
              </FunnelRate>
            </FunnelStep>
          ))}
        </FunnelGrid>
      </Card>

      <Card padding="lg">
        <SectionHeader
          title={`Daily activity${dashboard.range.days > 30 ? " · latest 30 days" : ""}`}
          description="Views and completions. Hover a bar to inspect its date and count."
        />
        <TrendChart>
          {trend.map((day) => (
            <TrendColumn
              key={day.day}
              title={`${day.day}: ${day.events.artifact_view} views, ${day.events.artifact_complete} completions`}
            >
              <TrendBars>
                <TrendBar
                  $height={(day.events.artifact_view / trendMax) * 100}
                />
                <TrendBar
                  $accent
                  $height={(day.events.artifact_complete / trendMax) * 100}
                />
              </TrendBars>
            </TrendColumn>
          ))}
        </TrendChart>
        <TrendLegend>
          <span>Views</span>
          <span>Completions</span>
        </TrendLegend>
      </Card>

      <Card padding="none">
        <div style={{ padding: "var(--space-5) var(--space-5) 0" }}>
          <SectionHeader
            title="Published artifacts"
            description="Ranked by public views in the selected range."
          />
        </div>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>Artifact</th>
                <th>Status</th>
                <th>Views</th>
                <th>Complete</th>
                <th>Saves</th>
                <th>Joins</th>
                <th>Published</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dashboard.artifacts.map((artifact) => (
                <tr key={artifact.id}>
                  <td>
                    <ArtifactName>
                      <strong>{artifact.title}</strong>
                      <span>
                        {kindLabel(artifact.kind)}
                        {artifact.source.planTitle
                          ? ` · ${artifact.source.planTitle}`
                          : ""}
                      </span>
                    </ArtifactName>
                  </td>
                  <td>
                    <Tag
                      size="sm"
                      withDot
                      variant={
                        artifact.status === "active" ? "success" : "neutral"
                      }
                    >
                      {artifact.status}
                    </Tag>
                  </td>
                  <td>{artifact.totals.artifact_view.toLocaleString()}</td>
                  <td>
                    {rate(
                      artifact.totals.artifact_complete,
                      artifact.totals.artifact_view
                    )}
                  </td>
                  <td>{artifact.totals.artifact_remix_created.toLocaleString()}</td>
                  <td>
                    {artifact.totals.artifact_community_join_attributed.toLocaleString()}
                  </td>
                  <td>{formatDate(artifact.publishedAt)}</td>
                  <td>
                    <RowActions>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!artifact.url || artifact.status !== "active"}
                        onClick={() => copyLink(artifact)}
                      >
                        {copiedId === artifact.id ? "Copied" : "Copy"}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={!artifact.url || artifact.status !== "active"}
                        onClick={() =>
                          artifact.url &&
                          window.open(artifact.url, "_blank", "noopener")
                        }
                      >
                        Open
                      </Button>
                      {dashboard.canEdit && artifact.status === "active" && (
                        <Button
                          size="sm"
                          variant="danger"
                          isLoading={revokingId === artifact.id}
                          onClick={() => revoke(artifact)}
                        >
                          Revoke
                        </Button>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </Card>

      <SplitGrid>
        <Card padding="lg">
          <SectionHeader
            title="Top source lessons"
            description="Which lessons create artifacts that acquire members."
          />
          {dashboard.sources.slice(0, 8).map((source) => (
            <RankingRow key={source.key}>
              <div>
                <strong>{source.stepTitle}</strong>
                <small>
                  {source.planTitle} · {source.publications} publication
                  {source.publications === 1 ? "" : "s"}
                </small>
              </div>
              <RankingMetrics>
                {source.totals.artifact_view.toLocaleString()} views
                <small>
                  {source.totals.artifact_community_join_attributed.toLocaleString()} joins ·{" "}
                  {rate(
                    source.totals.artifact_community_join_attributed,
                    source.totals.artifact_view
                  )}
                </small>
              </RankingMetrics>
            </RankingRow>
          ))}
        </Card>

        <Card padding="lg">
          <SectionHeader
            title="Formats"
            description="Performance by artifact engine."
          />
          {dashboard.kinds.map((kind) => (
            <RankingRow key={kind.kind}>
              <div>
                <strong>{kindLabel(kind.kind)}</strong>
                <small>
                  {kind.publications} publication
                  {kind.publications === 1 ? "" : "s"}
                </small>
              </div>
              <RankingMetrics>
                {kind.totals.artifact_view.toLocaleString()} views
                <small>
                  {rate(
                    kind.totals.artifact_complete,
                    kind.totals.artifact_view
                  )}{" "}
                  complete
                </small>
              </RankingMetrics>
            </RankingRow>
          ))}
        </Card>
      </SplitGrid>

      {dashboard.truncated && (
        <Card padding="sm">
          <MetricHint>
            Showing up to 200 publications in this dashboard.
          </MetricHint>
        </Card>
      )}
    </SectionStack>
  );
};

export default ArtifactTab;
